import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Spin } from 'antd'
import Avatar from 'boring-avatars'
import { type ComponentType, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import CheckIcon from '@/assets/icons/check.svg?react'
import ExternalLinkIcon from '@/assets/icons/external-link.svg?react'
import LogoutIcon from '@/assets/icons/logout.svg?react'
import PersonIcon from '@/assets/icons/person.svg?react'
import { Trans } from '@/components'
import { Divider } from '@/components/base/Divider'
import { message } from '@/components/base/Notification'
import { GLOWS_ACCOUNT_URL } from '@/consts/links'
import { AppContext } from '@/contexts/AppContext'
import type { ThemePreference } from '@/contexts/ThemeContext'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useAppContext } from '@/hooks/useAppContext'
import { EduVerify, EduVerifyStatus } from '@/models/profile.ts'
import { CreateTeamModal, TeamPlan } from '@/pages/teams/components/CreateTeamModal'
import { TeamCheckoutModal } from '@/pages/teams/components/TeamCheckoutModal'
import { TeamCreatedSuccessModal } from '@/pages/teams/components/TeamCreatedSuccessModal'
import { TeamDetailFormModal } from '@/pages/teams/components/TeamDetailFormModal'
import { DurationOption, TeamDurationModal } from '@/pages/teams/components/TeamDurationModal'
import {
  useAccountsLoaded,
  useAccountsStore,
  useActiveAccountId,
  useStoredAccounts,
} from '@/stores/accounts'
import { useAuthStore } from '@/stores/auth'
import { useIsTeamOnlyUser, useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDatePreset } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { AccountSwitcher } from './AccountSwitcher'
import { LanguageSwitcher } from './LanguageSwitcher'
import { TeamSwitcher } from './TeamSwitcher'
import { UserDropdownItem } from './UserDropdownItem'
import { UserProfileSection } from './UserProfileSection'

interface UserDropdownProps {
  onAddAccount?: () => void
  eduVerify?: EduVerify
}

const THEME_OPTIONS: Array<{
  value: ThemePreference
  labelKey: string
  icon: ComponentType<{ className?: string }>
}> = [
  {
    value: 'system',
    labelKey: 'header.userDropdown.appearance.system',
    icon: DesktopOutlined,
  },
  {
    value: 'light',
    labelKey: 'header.userDropdown.appearance.light',
    icon: SunOutlined,
  },
  {
    value: 'dark',
    labelKey: 'header.userDropdown.appearance.dark',
    icon: MoonOutlined,
  },
]

function ThemePreferenceMenuItem() {
  const { t } = useTranslation()
  const { setThemePreference, themePreference } = useThemeMode()
  const [showThemeMenu, setShowThemeMenu] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const selectedThemeOption =
    THEME_OPTIONS.find((option) => option.value === themePreference) ?? THEME_OPTIONS[0]
  const CurrentThemeIcon = selectedThemeOption.icon

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setShowThemeMenu(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowThemeMenu(false)
    }, 100)
  }

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <UserDropdownItem
        icon={CurrentThemeIcon}
        label={t('header.userDropdown.appearance.label')}
        active={showThemeMenu}
        trailing={
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="t-xs text-tertiary">{t(selectedThemeOption.labelKey)}</span>
            <ArrowRightIcon className="h-4 w-4 text-tertiary" />
          </div>
        }
      />

      <div
        className={cn(
          'absolute top-0 -left-4 w-[220px] -translate-x-full rounded-2xl bg-panel-white p-2 py-2 shadow-card-100',
          showThemeMenu ? 'block' : 'hidden',
        )}
      >
        {THEME_OPTIONS.map((option) => {
          const Icon = option.icon
          const isActive = themePreference === option.value

          return (
            <div
              key={option.value}
              className={cn(
                'flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 hover:bg-panel-gray-100',
                isActive && 'text-primary',
              )}
              onClick={(e) => {
                e.stopPropagation()
                setThemePreference(option.value)
              }}
            >
              <div className="flex items-center gap-2">
                <Icon className="text-secondary" />
                <span className="t-sm">{t(option.labelKey)}</span>
              </div>
              {isActive && <CheckIcon className="h-4 w-4 text-tertiary" />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function UserDropdown({ onAddAccount, eduVerify }: UserDropdownProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const teamStore = useTeamStore()
  const accountsStore = useAccountsStore()
  const accounts = useStoredAccounts()
  const activeAccountId = useActiveAccountId()
  const isAccountsLoaded = useAccountsLoaded()
  const isTeamOnlyUser = useIsTeamOnlyUser()
  const app = useContext(AppContext)
  const { isInPlatform, isOnTeamPage, hasPlatformToken, hasValidTeamToken } = useAppContext()

  // Teams state - 4 step flow
  const [showPlanModal, setShowPlanModal] = useState(false) // Step 1
  const [showTeamDetailModal, setShowTeamDetailModal] = useState(false) // Step 2
  const [showDurationModal, setShowDurationModal] = useState(false) // Step 3
  const [showCheckoutModal, setShowCheckoutModal] = useState(false) // Step 4
  const [showSuccessModal, setShowSuccessModal] = useState(false) // Success page

  const [selectedPlan, setSelectedPlan] = useState<TeamPlan | null>(null)
  const [teamFormData, setTeamFormData] = useState<{ name: string; description: string } | null>(
    null,
  )
  const [selectedDuration, setSelectedDuration] = useState<DurationOption | null>(null)
  const [createdTeamData, setCreatedTeamData] = useState<{
    resID: string
    teamName: string
    domainName: string
    adminName: string
    adminPassword: string
  } | null>(null)
  const [isCreatingTeam, setIsCreatingTeam] = useState(false)

  // Dropdown visibility state (replaces CSS group-hover)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Initialize accounts store when component mounts
  // Skip for team-only users since they don't use multi-account feature
  useEffect(() => {
    if (!isAccountsLoaded && !isTeamOnlyUser) {
      accountsStore.initialize()
    }
  }, [isAccountsLoaded, accountsStore, isTeamOnlyUser])

  const currentAccount = accounts.find((acc) => acc.id === activeAccountId)

  const handleAddAccount = () => {
    if (onAddAccount) {
      onAddAccount()
    } else {
      // Set multi-account mode in sessionStorage to persist across OAuth redirects
      sessionStorage.setItem('isMultiAccountMode', '1')
      window.location.href = '/login?multi-account=1'
    }
  }

  const handleLogout = async () => {
    if (isOnTeamPage) {
      // Team page logout: clear team-related state and redirect
      teamStore.clearTeamContext()
      teamStore.clearPrimaryTeamContext()
      teamStore.clearLoginSourceState()

      window.location.href = hasPlatformToken ? '/create' : '/team/login'
      return
    }

    // Platform page logout: handle multi-account logic
    const otherAccounts = accounts.filter((acc) => acc.id !== activeAccountId)

    if (otherAccounts.length > 0) {
      try {
        await accountsStore.removeAccountById(activeAccountId!)
      } catch (error) {
        console.error('Failed to logout current account:', error)
      }
    } else {
      try {
        await authStore.logoutAllAccounts()
      } catch (error) {
        console.error('Failed to logout all accounts:', error)
        authStore.logout()
      }
      window.location.href = '/login'
    }
  }

  // Teams handlers - 4 step flow

  const handleJoinTeam = () => {
    setShowDropdown(false)
    window.location.href = '/join-team'
  }

  // Entry point: Start with Step 1 (Plan selection)
  const handleCreateTeam = () => {
    setShowDropdown(false)
    setShowPlanModal(true)
  }

  // Step 1 → Step 2
  const handlePlanSelected = (plan: TeamPlan) => {
    setSelectedPlan(plan)
    setShowPlanModal(false)
    setShowTeamDetailModal(true)
  }

  // Step 2 back to Step 1
  const handleDetailBack = () => {
    setShowTeamDetailModal(false)
    setShowPlanModal(true)
  }

  // Step 2 → Step 3
  const handleTeamDetailNext = (data: { name: string; description: string }) => {
    setTeamFormData(data)
    setShowTeamDetailModal(false)
    setShowDurationModal(true)
  }

  // Step 3 back to Step 2
  const handleDurationBack = () => {
    setShowDurationModal(false)
    setShowTeamDetailModal(true)
  }

  // Step 3 → Step 4
  const handleDurationConfirm = (duration: DurationOption) => {
    setSelectedDuration(duration)
    setShowDurationModal(false)
    setShowCheckoutModal(true)
  }

  // Step 4 back to Step 3
  const handleCheckoutBack = () => {
    setShowCheckoutModal(false)
    setShowDurationModal(true)
  }

  // Step 4 → Create Team → Success
  const handleCheckoutConfirm = async () => {
    if (!teamFormData || !selectedPlan || !selectedDuration) return

    setIsCreatingTeam(true)
    try {
      const response = await API.createTeamOrder({
        info: JSON.stringify({
          name: teamFormData.name,
          intro: teamFormData.description,
        }),
        qty: selectedDuration.months,
        res_id: selectedPlan.id,
      })

      // Only handle success case, errors are handled by http.ts interceptor
      if (response.code === 0) {
        // detail?.team?.teamInfo — defensive check since /order is generic API
        const teamInfo = response.detail?.team?.teamInfo
        if (teamInfo) {
          message.success(t('team.messages.createSuccess'))
          setCreatedTeamData(teamInfo)
          setShowCheckoutModal(false)
          setShowSuccessModal(true)

          // Invalidate team list query to trigger automatic refetch
          queryClient.invalidateQueries({ queryKey: ['teamList'] })
        }
      }
      // code !== 0 cases are handled by http.ts interceptor (auto show error message)
    } catch (error) {
      // Only handle network errors or unexpected exceptions
      console.error('Network error:', error)
    } finally {
      setIsCreatingTeam(false)
    }
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)

    // Reset all modals and form data
    setShowPlanModal(false)
    setShowTeamDetailModal(false)
    setShowDurationModal(false)
    setShowCheckoutModal(false)
    setSelectedPlan(null)
    setTeamFormData(null)
    setSelectedDuration(null)
    setCreatedTeamData(null)
  }

  // Any modal open means dropdown should stay hidden
  const isAnyModalOpen =
    showPlanModal ||
    showTeamDetailModal ||
    showDurationModal ||
    showCheckoutModal ||
    showSuccessModal

  // Dropdown hover handlers (with timeout to allow cursor to reach ActionMenu popup)
  const handleDropdownMouseEnter = () => {
    if (isAnyModalOpen) return
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    setShowDropdown(true)
  }

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false)
    }, 150)
  }

  // Handle edge case: no accounts or invalid user data
  // Team-only users don't use multi-account feature, skip accounts loading check
  if (!isTeamOnlyUser && !isAccountsLoaded) {
    return (
      <div className="flex items-center space-x-2">
        <Spin size="small" />
        <span className="t-sm text-secondary">Loading...</span>
      </div>
    )
  }

  // For team-only users: check if they have a valid team token
  // For platform users: check both currentAccount and userStore
  const shouldShowLogin = isTeamOnlyUser
    ? !hasValidTeamToken
    : (!currentAccount && userStore.id === 0) || !userStore.name

  if (shouldShowLogin) {
    return (
      <Button type="primary" onClick={handleAddAccount} className="bg-blue-600 hover:bg-blue-700">
        Login
      </Button>
    )
  }

  // Team users with valid token but userStore not loaded yet - show loading
  if (isTeamOnlyUser && hasValidTeamToken && (!userStore.id || !userStore.name)) {
    return (
      <div className="flex items-center space-x-2">
        <Spin size="small" />
        <span className="t-sm text-secondary">Loading...</span>
      </div>
    )
  }

  // Handle edge case: user is logged in but accounts are not synced
  // Skip this check for team-only users since they don't use multi-account feature
  if (userStore.id > 0 && accounts.length === 0 && !isTeamOnlyUser) {
    console.warn('User is logged in but no accounts found in store')
    return (
      <div className="flex items-center space-x-2">
        <Avatar size={32} name={userStore.name.split('@')[0]} variant="beam" colors={['#525866']} />
        <span className="t-sb truncate flex-1 min-w-0">
          {userStore.nickname || userStore.alias}
        </span>
        <Button
          size="small"
          type="text"
          onClick={() => accountsStore.initialize()}
          className="text-xs flex-shrink-0"
        >
          Refresh
        </Button>
      </div>
    )
  }

  return (
    <div
      className="relative"
      data-ai-component="user-profile-dropdown"
      data-ai-action="open-user-menu"
      data-ai-value={userStore.nickname || userStore.alias || userStore.name}
      onMouseEnter={handleDropdownMouseEnter}
      onMouseLeave={handleDropdownMouseLeave}
    >
      <div
        className={cn(
          'flex items-center space-x-2 cursor-pointer rounded-lg p-1',
          showDropdown && 'bg-neutral-50',
        )}
      >
        <Avatar size={32} name={userStore.name.split('@')[0]} variant="beam" colors={['#525866']} />
        <span className="t-sb truncate flex-1 min-w-0 max-w-[120px]">
          {userStore.nickname || userStore.alias}
        </span>
        <ArrowDownIcon
          className={cn(
            'w-4 h-4 transition-transform duration-300 flex-shrink-0',
            showDropdown && 'rotate-180',
          )}
        />
      </div>

      <div
        className={cn(
          'absolute top-full right-0 mt-2 w-[280px] bg-panel-white rounded-2xl shadow-card-100 transition-all duration-200',
          showDropdown ? 'opacity-100 visible' : 'opacity-0 invisible',
        )}
      >
        <div className="flex flex-col gap-4 p-4">
          {/* Current Account Info */}
          <div className="flex flex-col justify-center gap-4">
            <div className="flex items-center gap-x-4">
              <Avatar
                size={48}
                name={userStore.name.split('@')[0]}
                variant="beam"
                colors={['#525866']}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="t-sb truncate">{userStore.nickname || userStore.alias}</div>
                <div className="t-xs text-secondary truncate">{userStore.name}</div>
              </div>
            </div>

            {eduVerify?.status === EduVerifyStatus.Verified && (
              <div className="flex gap-x-4">
                <div className="flex p-2 bg-neutral-100 rounded-lg gap-x-2 t-x-sb text-primary">
                  Education
                </div>
                <div className="flex p-2 bg-neutral-100 rounded-lg gap-x-2 t-x-sb text-primary">
                  {formatDatePreset((eduVerify.graduationTime?.seconds ?? 0) * 1000, 'day')}
                </div>
              </div>
            )}
          </div>

          <Divider className="my-0" />

          {/* Setting List */}
          <div className="flex flex-col gap-2">
            <UserProfileSection />

            <LanguageSwitcher onLanguageChange={() => app.refresh()} />

            <ThemePreferenceMenuItem />

            {/* Teams Switcher */}
            <TeamSwitcher onJoinTeam={handleJoinTeam} onCreateTeam={handleCreateTeam} />
          </div>

          <Divider className="my-0" />

          {/* Account List */}
          <div className="flex flex-col gap-2">
            <a className="block" href={GLOWS_ACCOUNT_URL} target="_blank" rel="noopener noreferrer">
              <UserDropdownItem
                icon={PersonIcon}
                label={<Trans i18nKey="header.userDropdown.glowsProfile">Glows Profile</Trans>}
                trailing={<ExternalLinkIcon className="h-4 w-4 shrink-0 text-tertiary" />}
              />
            </a>

            {/* Show AccountSwitcher only in platform context (not on team pages) and for platform users */}
            {isInPlatform && !isTeamOnlyUser && (
              <AccountSwitcher
                accounts={accounts}
                activeAccountId={activeAccountId}
                onAddAccount={handleAddAccount}
                onSwitchAccount={(accountId) => accountsStore.switchAccount(accountId)}
                onRemoveAccount={(accountId) => accountsStore.removeAccountById(accountId)}
              />
            )}

            {/* Logout */}
            <UserDropdownItem
              icon={LogoutIcon}
              label={<Trans i18nKey="header.userDropdown.logout">Log out</Trans>}
              onClick={handleLogout}
            />
          </div>
        </div>
      </div>

      {/* Step 1: Plan Selection Modal */}
      <CreateTeamModal
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onPlanSelected={handlePlanSelected}
      />

      {/* Step 2: Team Detail Form Modal */}
      <TeamDetailFormModal
        open={showTeamDetailModal}
        onClose={() => setShowTeamDetailModal(false)}
        onBack={handleDetailBack}
        onNext={handleTeamDetailNext}
      />

      {/* Step 3: Duration Selection Modal */}
      <TeamDurationModal
        open={showDurationModal}
        onClose={() => setShowDurationModal(false)}
        onBack={handleDurationBack}
        onConfirm={handleDurationConfirm}
        pricePerMonth={selectedPlan?.pricePerMonth ?? 0}
      />

      {/* Step 4: Checkout Modal */}
      <TeamCheckoutModal
        open={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        onBack={handleCheckoutBack}
        onConfirm={handleCheckoutConfirm}
        loading={isCreatingTeam}
        teamData={{
          plan: selectedPlan,
          duration: selectedDuration,
          name: teamFormData?.name || '',
          description: teamFormData?.description || '',
        }}
      />

      {/* Team Created Success Modal */}
      <TeamCreatedSuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        teamData={createdTeamData}
      />
    </div>
  )
}
